import { useCallback, useState } from 'react'
import type { MapViewState } from 'tripPlanner/components/Chat/chatMapConstants'
import type { MapBounds } from 'tripPlanner/utils/mapBounds'
import type { HotelMapViewData, ExperienceMapViewData } from 'tripPlanner/api/types'
import { chatExploreCategories, type ChatExploreCategory, type LEExploreCategorySource } from 'tripPlanner/pages/chat/chatExploreCategories'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'
import { useChatMapExploreFilters } from './useChatMapExploreFilters'
import type { RightPanelView } from './useRightPanelState'

const HOTELS_EXPLORE_CATEGORY = chatExploreCategories.find(c => c.source === 'le-hotels')!
const EXPERIENCES_EXPLORE_CATEGORY = chatExploreCategories.find(c => c.source === 'le-experiences')!

interface UseMapFilterStateParams {
  /** Switch the right panel view (from useRightPanelState) */
  setRightPanelView: (view: RightPanelView) => void
  /** Whether the hotel search filter overlay is active (from useSearchResults) */
  isHotelFilterVisible: boolean
  /** Hide the hotel filter overlay (from useSearchResults) */
  hideHotelFilter: () => void
  /** Populate hotel search results from carousel "Map view" click (from useSearchResults) */
  setHotelSearchResultsFromCarousel: (data: HotelMapViewData) => void
  /** Whether the experience search filter overlay is active (from useSearchResults) */
  isExperienceFilterVisible: boolean
  /** Hide the experience filter overlay (from useSearchResults) */
  hideExperienceFilter: () => void
  /** Populate experience search results from carousel "Map view" click (from useSearchResults) */
  setExperienceSearchResultsFromCarousel: (data: ExperienceMapViewData) => void
}

interface UseMapFilterStateReturn {
  // Map view state (needed by explore filters for viewport eligibility)
  mapViewState: MapViewState | undefined
  mapBounds: MapBounds | undefined
  handleMapViewStateChange: (viewState: MapViewState) => void
  handleMapBoundsChange: (bounds: MapBounds) => void

  // Filter dismiss + coordination handlers
  handleHotelFilterDismiss: () => void
  handleExperienceFilterDismiss: () => void
  handleExploreCategorySelect: (category: ChatExploreCategory | undefined) => void
  applyHotelFilterState: (data: HotelMapViewData) => void
  applyExperienceFilterState: (data: ExperienceMapViewData) => void
  handleExploreMapFromDrawer: (source: LEExploreCategorySource) => void

  // Search this area
  handleSearchThisAreaClick: () => void
  shouldShowSearchThisArea: boolean
  isSearchThisAreaDisabled: boolean

  // Computed map props
  deemphasizeBaseMarkers: boolean
  disableClustering: boolean

  // Forwarded from useChatMapExploreFilters (needed by JSX)
  selectedExploreCategory: ChatExploreCategory | undefined
  exploreMarkers: Array<MapMarkerComponent>
  exploreOffers: Array<ExploreOffer>
  isExploreFilterVisible: boolean
  isExploreFilterActive: boolean
  isExploreLoading: boolean
  isExploreOffersError: boolean
  refetchExploreOffers: () => void
  hasExploreResults: boolean
  isBoundsDirty: boolean

  /** Reset all filter-related state (used when switching chats) */
  resetFilterState: () => void
}

export function useMapFilterState({
  setRightPanelView,
  isHotelFilterVisible,
  hideHotelFilter,
  setHotelSearchResultsFromCarousel,
  isExperienceFilterVisible,
  hideExperienceFilter,
  setExperienceSearchResultsFromCarousel,
}: UseMapFilterStateParams): UseMapFilterStateReturn {
  // ── Map view state ──────────────────────────────────────────────────
  const [mapViewState, setMapViewState] = useState<MapViewState | undefined>(undefined)
  const [mapBounds, setMapBounds] = useState<MapBounds | undefined>(undefined)

  // ── Explore filter (encapsulated) ───────────────────────────────────
  const {
    selectedCategory: selectedExploreCategory,
    selectCategory: selectExploreCategory,
    exploreMarkers,
    exploreOffers,
    isExploreFilterVisible,
    isExploreFilterActive,
    isExploreLoading,
    isExploreOffersError,
    refetchExploreOffers,
    hasExploreResults,
    isBoundsDirty,
    applyBounds,
    reset: resetExploreFilters,
  } = useChatMapExploreFilters({
    mapViewState,
    mapBounds,
    isHotelFilterVisible,
    isExperienceFilterVisible,
  })

  // ── Handlers ────────────────────────────────────────────────────────
  const handleMapViewStateChange = useCallback((viewState: MapViewState) => {
    setMapViewState(viewState)
  }, [])

  const handleMapBoundsChange = useCallback((bounds: MapBounds) => {
    setMapBounds(bounds)
  }, [])

  const handleSearchThisAreaClick = useCallback(() => {
    applyBounds()
  }, [applyBounds])

  const handleHotelFilterDismiss = useCallback(() => {
    hideHotelFilter()
  }, [hideHotelFilter])

  const handleExploreCategorySelect = useCallback((category: ChatExploreCategory | undefined) => {
    selectExploreCategory(category)
  }, [selectExploreCategory])

  const applyHotelFilterState = useCallback((data: HotelMapViewData) => {
    hideExperienceFilter()
    setRightPanelView('map')
    setHotelSearchResultsFromCarousel(data)
  }, [hideExperienceFilter, setRightPanelView, setHotelSearchResultsFromCarousel])

  const handleExperienceFilterDismiss = useCallback(() => {
    hideExperienceFilter()
  }, [hideExperienceFilter])

  const applyExperienceFilterState = useCallback((data: ExperienceMapViewData) => {
    hideHotelFilter()
    setRightPanelView('map')
    setExperienceSearchResultsFromCarousel(data)
  }, [hideHotelFilter, setRightPanelView, setExperienceSearchResultsFromCarousel])

  const handleExploreMapFromDrawer = useCallback((source: LEExploreCategorySource) => {
    const exploreCategory = source === 'le-experiences'
      ? EXPERIENCES_EXPLORE_CATEGORY
      : HOTELS_EXPLORE_CATEGORY

    setRightPanelView('map')
    hideHotelFilter()
    hideExperienceFilter()
    selectExploreCategory(exploreCategory, true)
  }, [hideExperienceFilter, hideHotelFilter, setRightPanelView, selectExploreCategory])

  // ── Reset all filter state (chat switch / new chat) ──────────────────
  const resetFilterState = useCallback(() => {
    // Clear viewport shadow state so explore filters don't briefly use stale
    // bounds/zoom from the previous chat (prevents category pills flashing).
    setMapViewState(undefined)
    setMapBounds(undefined)
    resetExploreFilters()
  }, [resetExploreFilters])

  // ── Computed values ─────────────────────────────────────────────────
  const shouldShowSearchThisArea = isExploreFilterActive && isBoundsDirty
  const isSearchThisAreaDisabled = isExploreLoading
  const deemphasizeBaseMarkers = isHotelFilterVisible || isExperienceFilterVisible || isExploreFilterActive
  const disableClustering = isHotelFilterVisible || isExperienceFilterVisible || isExploreFilterActive

  return {
    mapViewState,
    mapBounds,
    handleMapViewStateChange,
    handleMapBoundsChange,

    handleHotelFilterDismiss,
    handleExperienceFilterDismiss,
    handleExploreCategorySelect,
    applyHotelFilterState,
    applyExperienceFilterState,
    handleExploreMapFromDrawer,

    handleSearchThisAreaClick,
    shouldShowSearchThisArea,
    isSearchThisAreaDisabled,

    deemphasizeBaseMarkers,
    disableClustering,

    selectedExploreCategory,
    exploreMarkers,
    exploreOffers,
    isExploreFilterVisible,
    isExploreFilterActive,
    isExploreLoading,
    isExploreOffersError,
    refetchExploreOffers,
    hasExploreResults,
    isBoundsDirty,

    resetFilterState,
  }
}
